import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, Home, ArrowRight } from "lucide-react";
import axios from "axios";
import API from "@/apiConfig";

export default function CollectivesTeaser() {
  const [collectives, setCollectives] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/collectives`)
      .then((r) => {
        const all = Array.isArray(r.data) ? r.data : [];
        const filtered = all.filter(
          (c) => c.is_restricted !== true
        );
        setCollectives(
          filtered.map((c) => ({
            id: c.id,
            slug: c.slug || c.id,
            name: c.display_name || c.name || "Collective",
            leader: c.host_name || "Apostolic Oversight",
            memberCount: c.member_count || 0,
            shortDescription: c.description || "",
            schedule: c.meeting_schedule || "",
            area: c.area || "",
          }))
        );
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <section className="py-20 md:py-32 bg-navy">
        <div className="container-page">
          <div className="flex gap-6 overflow-hidden">
            {[1, 2, 3].map((i) => (
              <div key={i} className="shrink-0 w-[80vw] sm:w-[300px] lg:flex-1 h-48 bg-bone/10 rounded-2xl animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (collectives.length === 0) return null;

  return (
    <section data-testid="collectives-teaser-section" className="py-20 md:py-32 bg-navy text-bone relative overflow-hidden">
      {/* Decorative background blur */}
      <div className="absolute top-1/2 right-0 w-[600px] h-[600px] bg-gold/5 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/3 pointer-events-none" />

      <div className="container-page relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left Column: Heading and Intro */}
          <div className="lg:col-span-4 lg:pr-8">
            <div className="flex items-center gap-2 mb-4">
              <Home className="h-5 w-5 text-gold" />
              <span className="text-gold text-sm font-heading uppercase tracking-widest font-bold">
                Church in the Home
              </span>
            </div>
            
            <h2 className="font-heading text-4xl md:text-5xl text-bone mb-6 leading-tight">
              Our Collectives
            </h2>
            
            <div className="w-12 h-1 bg-gold rounded-full mb-6 opacity-80" />
            
            <p className="text-bone/80 text-base leading-relaxed mb-6">
              Church is not a building. It's people gathering in homes — worshipping, praying, breaking bread together, and growing in the Word.
            </p>
            
            <p className="text-gold/80 italic font-heading text-lg mb-8 border-l-2 border-gold/40 pl-4">
              "Day by day, continuing with one mind in the temple, and breaking bread from house to house..."
            </p>

            <Link to="/collectives" className="hidden lg:inline-block">
              <Button 
                data-testid="find-collective-btn-desktop" 
                className="bg-gold text-navy hover:bg-gold/90 shadow-[0_0_15px_rgba(212,175,55,0.2)] hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all duration-300 hover:-translate-y-0.5 rounded-full px-8 h-12 uppercase tracking-wider font-bold text-xs"
              >
                Find Your Collective
              </Button>
            </Link>
          </div>

          {/* Right Column: Carousel/Grid of Cards */}
          <div className="lg:col-span-8">
            {/* Desktop Grid / Mobile Horizontal Scroll */}
            <div className="flex overflow-x-auto lg:grid lg:grid-cols-3 gap-6 pb-8 pt-4 -mx-4 px-4 lg:mx-0 lg:px-0 lg:pb-0 lg:pt-0 snap-x snap-mandatory scrollbar-hide">
              {collectives.map((coll) => (
                <Link 
                  to={`/collectives/${coll.slug}`} 
                  key={coll.id} 
                  className="snap-center shrink-0 w-[80vw] sm:w-[300px] lg:w-auto group block h-full"
                >
                  <Card className="h-full bg-bone border border-gold/30 shadow-md hover:shadow-xl hover:-translate-y-1.5 transition-all duration-500 rounded-2xl flex flex-col relative overflow-hidden">
                    {/* Subtle hover background effect */}
                    <div className="absolute inset-0 bg-gradient-to-br from-gold/0 to-gold/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    
                    <CardContent className="p-6 flex-1 flex flex-col relative z-10">
                      <div className="w-10 h-10 rounded-full bg-navy/5 flex items-center justify-center mb-4 group-hover:bg-gold/10 transition-colors duration-300 border border-navy/10">
                        <Users className="h-5 w-5 text-navy" />
                      </div>
                      
                      <h3 className="font-heading text-2xl text-navy mb-2 group-hover:text-gold transition-colors duration-300">
                        {coll.name}
                      </h3>
                      
                      <p className="text-navy/60 text-xs font-bold uppercase tracking-wider mb-4">
                        Led by {coll.leader}
                      </p>
                      
                      <p className="text-gray-600 text-sm leading-relaxed mb-3 line-clamp-2">
                        {coll.shortDescription}
                      </p>
                      
                      <div className="mt-auto pt-4 border-t border-navy/10 flex items-center justify-between">
                        <span className="text-xs font-bold text-navy uppercase tracking-wider flex items-center gap-1.5">
                          <Users className="h-3.5 w-3.5" />
                          {coll.memberCount} Members
                        </span>
                        <div className="w-8 h-8 rounded-full bg-navy/5 flex items-center justify-center group-hover:bg-navy group-hover:text-gold transition-colors duration-300">
                          <ArrowRight className="h-4 w-4 text-navy group-hover:text-gold" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>

            {/* Mobile CTA */}
            <div className="mt-4 lg:hidden text-center">
              <Link to="/collectives">
                <Button 
                  data-testid="find-collective-btn-mobile" 
                  className="w-full bg-gold text-navy hover:bg-gold/90 shadow-md rounded-full h-12 font-bold uppercase tracking-wider text-xs"
                >
                  Find Your Collective
                </Button>
              </Link>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}